import { Grid, Typography } from '@material-ui/core';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from './device-menu-modal-styles';
import DeviceButton from '../device-button';
import DeviceMenuStatusIcon from '../device-menu-status-icon';
import BaseButton from 'src/common/components/base-button';
import EmeraldDialog from 'src/common/components/emerald-dialog/emerald-dialog';
import {
  DialogBar,
  showEmeraldDialog,
} from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { IDeviceStatusItem } from 'src/common/interfaces/states/IDeviceStatusItem';
import { AppState, store } from 'src/common/redux/configure-store';
import { Pagination } from 'src/pagination/pagination';
import { reInitDevices } from 'src/common/device/reinitialize-devices';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { DeviceNotificationActions } from 'src/common/redux/reducers/device-notification-reducer';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import clsx from 'clsx';
import { showDeviceStatusDialog } from 'src/device-status/device-status';
import { DeviceType } from 'src/common/enumerations/device-type';
import { HealthyStatus } from 'src/devices/jpos-healthy-status';

interface DeviceMenuModalProps {
  onFinish?: (arg: boolean) => void;
}
interface HealthCheckModalProps {
  deviceResponses: IDeviceStatusItem[];
}

export const showHealthCheckModal = (healthCheckResponses: IDeviceStatusItem[]): void => {
  const modalConfig: IMessageModal = {
    type: EmeraldDialogTypes.Status,
    title: 'HealthCheckModalTitle',
    onAccept: () => {
      popEmeraldModal('HealthCheckModal');
      showDeviceStatusDialog();
    },
    disableBackdropClick: true,
    hideCancelButton: true,
    acceptButtonLabel: 'HealthCheckMenuButton',
    size: MessageModalSizeEnum.Large,
    textContent: <TextContent deviceResponses={healthCheckResponses} />,
    modalId: 'HealthCheckModal',
    dialogTestIdPrefix: 'health-check-modal',
  };
  showEmeraldDialog(modalConfig);
};

export const GetDeviceName = (logicalDeviceName: string): string => {
  const devices = useSelector((state: AppState) => state.deviceStatus.devices);
  const { t } = useTranslation();
  let deviceName = t('JPOS_DEVICE_UNKNOWN');
  devices.forEach(device => {
    if (device.logicalName === logicalDeviceName) {
      deviceName = device.name;
    }
  });
  return deviceName;
};

export const TextContent = ({ deviceResponses }: HealthCheckModalProps): JSX.Element => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <Grid container direction='column' justifyContent='center'>
      <Grid item className={classes.healthCheckHeader}>
        <Typography variant='h2' data-testid='health-check-title'>
          {t('HealthCheckMenuTitle')}
        </Typography>
      </Grid>
      {deviceResponses.length > 0 ? (
        <Grid item className={classes.deviceItem} justifyContent='center'>
          {deviceResponses.map((response: IDeviceStatusItem, idx) => (
            <Grid key={idx} container direction='row' className={classes.messageDivider}>
              <Grid item className={classes.deviceName}>
                <Typography
                  className={classes.noOverflow}
                  data-testid={`health-check-device-name-${idx}`}
                >
                  {`${GetDeviceName(response.logicalName)}: `}
                </Typography>
              </Grid>
              <Grid item className={classes.deviceStatus}>
                <Typography
                  className={clsx(classes.messageLineStatus, classes.noOverflow)}
                  data-testid={`health-check-device-status-${idx}`}
                >
                  {`${
                    response.deviceStatus ? t(response.deviceStatus) : t('DefaultDeviceMessage')
                  }`}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid item className={classes.deviceItem} justifyContent='center'>
          <Typography data-testId='health-check-healthy-status'>
            {`${t('DefaultHealthCheckMessage')}`}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

const DeviceMenuModal: React.FC<DeviceMenuModalProps> = ({
  onFinish,
}: DeviceMenuModalProps): JSX.Element => {
  const devices = useSelector((state: AppState) => state.deviceStatus.devices);
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();
  const { t } = useTranslation();
  const [selectedDevice, setSelectedDevice] = useState<IDeviceStatusItem | undefined>();
  const [allDevicesConnected, setAllDevicesConnected] = useState<boolean>();
  const classes = useStyles();
  const handleDeviceClick = (device: IDeviceStatusItem) => (): void => {
    setSelectedDevice(device);
  };

  const dispatch = useDispatch();

  const verifyStatus = (status: string): string => {
    if (Object.keys(HealthyStatus).find(goodStatus => goodStatus === status)) {
      return status;
    }
    return '';
  };

  const handleHealthCheckClick = async (): Promise<void> => {
    popEmeraldModal('DeviceMenuModal');
    const healthCheckResponses: IDeviceStatusItem[] = store
      .getState()
      .deviceStatus.devices.filter(
        i => i.deviceStatus !== verifyStatus(i.deviceStatus) && i.deviceType !== DeviceType.CCL,
      );
    showHealthCheckModal(healthCheckResponses);
  };

  const handleCloseClick = (): void => {
    dispatch(DeviceNotificationActions.setShowingDeviceModal(false));
    popEmeraldModal();
    onFinish && onFinish(false);
  };
  const handleConnectClick = async (): Promise<void> =>
    withSpinner(() => reInitDevices(t, selectedDevice, undefined, onFinish));

  useEffect(() => {
    let device = devices.find(({ logicalName }) => logicalName === selectedDevice?.logicalName);
    if (!device?.warning) {
      device = devices.find(({ warning }) => warning);
    }
    setSelectedDevice(device);
    setAllDevicesConnected(!selectedDevice);
  }, [devices, selectedDevice]);

  return (
    <EmeraldDialog
      data-testid='device-menu-modal'
      open
      fullWidth
      classes={{
        paper: classes.dialogPaper,
        paperFullWidth: classes.dialogPaperFullWidth,
      }}
    >
      <Grid container alignItems='center' className={classes.header}>
        <Grid item className={classes.headerTitle}>
          <Typography>{t('DeviceMenuTitle')}</Typography>
        </Grid>
        <DeviceMenuStatusIcon operational={allDevicesConnected} className={classes.statusIcon} />
      </Grid>
      <Grid container direction='column' className={classes.contentContainer}>
        <Grid container item>
          <Grid container item className={classes.leftColumn}>
            <Grid
              container
              item
              ref={scrollRef}
              className={`${classes.deviceListContainer} verticalScroll`}
            >
              <Grid item className={classes.deviceList}>
                {devices.map(device => (
                  <DeviceButton
                    key={device.logicalName}
                    text={device.name}
                    selected={device.logicalName === selectedDevice?.logicalName}
                    type={device.deviceType}
                    operational={!device.warning}
                    onClick={handleDeviceClick(device)}
                  />
                ))}
              </Grid>
            </Grid>
          </Grid>
          <Grid container item justifyContent='center' className={classes.rightColumn}>
            {selectedDevice ? (
              <Grid
                data-testid='device-selected-message'
                container
                item
                className={classes.messageContainer}
              >
                <Typography variant='h2'>
                  {t('DeviceDisconnectedTitle', { arg: selectedDevice.name })}
                </Typography>
                <Typography className={classes.messageSubtitle}>
                  {t('DeviceDisconnectedSubtitle', { arg: selectedDevice.name })}
                </Typography>
                <BaseButton
                  buttonType='white'
                  className={classes.connectButton}
                  onClick={handleConnectClick}
                  data-testid='connect-button'
                >
                  {t('DeviceConnectionFailedButton')}
                </BaseButton>
              </Grid>
            ) : (
              <Grid
                data-testid='device-healthy-message'
                container
                item
                className={classes.messageContainer}
              >
                <Typography variant='h2'>{t('DevicesConnectedTitle')}</Typography>
                <Grid item className={classes.bar}>
                  <DialogBar type={EmeraldDialogTypes.InfoDecision} />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container item className={classes.bottomRow}>
          <Grid container item justifyContent='center' className={classes.leftColumn}>
            <Grid container item justifyContent='space-evenly' className={classes.pagination}>
              <Pagination
                currentPage={currentPage}
                totalPages={lastPage}
                updateCurrentPageHandler={onPageChanged}
                displayText={false}
                testIdPrefix={'device-status'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            justifyContent='flex-end'
            className={`${classes.rightColumn} ${classes.actionButtons}`}
          >
            <Grid item className={classes.healthButtonContainer}>
              <BaseButton
                data-testid='health-button'
                buttonType='white'
                onClick={handleHealthCheckClick}
                className={classes.healthButton}
              >
                {t('HealthCheckButtonText')}
              </BaseButton>
            </Grid>
            <Grid item>
              <BaseButton
                data-testid='close-button'
                buttonType='white'
                onClick={handleCloseClick}
                className={classes.closeButton}
              >
                {t('Close')}
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EmeraldDialog>
  );
};

export default DeviceMenuModal;
